<script setup lang="ts">
import { computed } from 'vue'
import type { DashboardAccountHealth } from '@/types/models'
import { linkedinAccountStatusLabels } from '@/lib/linkedinAccountStatus'

const props = defineProps<{
  account: DashboardAccountHealth
}>()

const statusLabels = linkedinAccountStatusLabels

const avatarStatus = computed(() =>
  props.account.status === 'connected' ? 'online' : 'busy',
)

const budgetUsedPct = computed(() => {
  if (props.account.daily_send_limit === 0) return 0
  return Math.round(Math.min(1, props.account.sent_today / props.account.daily_send_limit) * 100)
})

const windowLabel = computed(() => {
  const sendWindow = props.account.send_window
  if (!sendWindow) return 'No send window set'
  return `${sendWindow.start}–${sendWindow.end} ${sendWindow.tz}`
})
</script>

<template>
  <Card
    padding="md"
    class="min-w-[220px] flex-1"
  >
    <div class="flex items-center gap-3">
      <Avatar
        :name="props.account.display_name ?? 'Unnamed account'"
        color="var(--jp-color-neutral)"
        :status="avatarStatus"
      />
      <div class="min-w-0">
        <p class="truncate font-serif font-medium leading-tight text-on-surface">
          {{ props.account.display_name ?? 'Unnamed account' }}
        </p>
        <p class="text-metadata text-on-surface-variant">
          {{ statusLabels[props.account.status] ?? props.account.status }} · {{ windowLabel }}
        </p>
      </div>
    </div>

    <div class="mt-4">
      <div class="mb-1 flex justify-between text-metadata text-on-surface-variant">
        <span>Send budget</span>
        <span class="tabular-nums">{{ props.account.sent_today }} / {{ props.account.daily_send_limit }}</span>
      </div>
      <ProgressBar
        :value="budgetUsedPct"
        size="sm"
      />
    </div>

    <dl class="mt-3 flex justify-between text-metadata text-on-surface-variant">
      <div>
        <dt>Sent / failed</dt>
        <dd class="tabular-nums text-on-surface">
          {{ props.account.sent_count }} / {{ props.account.failed_count }}
        </dd>
      </div>
    </dl>
  </Card>
</template>
